import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../utils/api';
import './ApplyPage.css';

const DISTRICTS = ['Colombo','Gampaha','Kalutara','Kandy','Matale','Nuwara Eliya','Galle','Matara',
  'Hambantota','Jaffna','Kilinochchi','Mannar','Vavuniya','Mullaitivu','Batticaloa','Ampara',
  'Trincomalee','Kurunegala','Puttalam','Anuradhapura','Polonnaruwa','Badulla','Monaragala',
  'Ratnapura','Kegalle'];

const INDUSTRIES = ['Agriculture','Construction','Education','Finance & Banking','Food & Beverage',
  'Healthcare','Hospitality & Tourism','IT & Technology','Legal & Professional',
  'Logistics & Transport','Manufacturing','Retail & Wholesale','Real Estate','Textiles & Apparel','Other'];

const TOOLS = ['Excel / Google Sheets','WhatsApp','Paper forms','Phone calls','Existing software','No tools — fully manual'];
const IMPROVEMENTS = ['Save employee time','Reduce errors & rework','Increase sales','Improve customer service','Better reporting & insights','Better compliance','Reduce costs'];
const BROAD_IMPACT = ['Create more jobs','Enable faster business growth','Improve customer service','Positive community impact','Drive industry innovation','Support underserved communities'];
const SERVICES = ['CRM','WhatsApp Business Integration','AI Chatbots','Inventory Management','HR Systems',
  'Lead Management','Customer Support Automation','Fleet Management','Reporting Dashboards','Custom Software Development','Other'];

const STEPS = ['Company Details','Business Challenge','Expected Impact','Justification','Consent'];

const INITIAL = {
  company_name:'', industry:'', contact_person:'', designation:'',
  mobile_number:'', email:'', district:'', employee_count:'', website_url:'', business_registration_number:'',
  manual_process_problem:'', current_process:'', weekly_time_spent:'', estimated_monthly_loss:'', current_tools:[],
  expected_automation_impact:'', measurable_improvements:[],
  selection_reason:'', expected_business_impact:[],
  future_investment_interest:'', publicity_consent:'', consultation_interest:false, service_interests:[],
};

function CheckItem({ label, checked, onChange, type='checkbox' }) {
  return (
    <label className={`choice-item${checked?' selected':''}`}>
      <input type={type} checked={checked} onChange={onChange} />
      <span className="choice-label">{label}</span>
    </label>
  );
}

function Field({ label, required, children, hint, error }) {
  return (
    <div className="field">
      <label>{label}{required && <span className="req"> *</span>}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
      {error && <div className="err-msg">{error}</div>}
    </div>
  );
}

export default function ApplyPage() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const set = (field, value) => setData(d => ({ ...d, [field]: value }));

  const toggleArr = (field, value) => setData(d => ({
    ...d, [field]: d[field].includes(value)
      ? d[field].filter(v => v !== value)
      : [...d[field], value]
  }));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!data.company_name.trim()) e.company_name = 'Required';
      if (!data.industry) e.industry = 'Required';
      if (!data.contact_person.trim()) e.contact_person = 'Required';
      if (!data.mobile_number.trim()) e.mobile_number = 'Required';
      else if (!/^0[0-9]{9}$/.test(data.mobile_number.replace(/\s/g,''))) e.mobile_number = 'Enter a valid Sri Lankan number (e.g. 0771234567)';
      if (!data.email.trim()) e.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Invalid email';
      if (!data.district) e.district = 'Required';
      if (!data.employee_count) e.employee_count = 'Required';
    }
    if (step === 1) {
      if (!data.manual_process_problem.trim()) e.manual_process_problem = 'Required';
      if (!data.current_process.trim()) e.current_process = 'Required';
      if (!data.weekly_time_spent.trim()) e.weekly_time_spent = 'Required';
    }
    if (step === 2) {
      if (!data.expected_automation_impact.trim()) e.expected_automation_impact = 'Required';
      if (!data.measurable_improvements.length) e.measurable_improvements = 'Select at least one';
    }
    if (step === 3) {
      if (!data.selection_reason.trim()) e.selection_reason = 'Required';
    }
    if (step === 4) {
      if (!data.future_investment_interest) e.future_investment_interest = 'Required';
      if (data.publicity_consent === '') e.publicity_consent = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const back = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        publicity_consent: data.publicity_consent === 'yes',
        consultation_interest: !!data.consultation_interest,
        employee_count: parseInt(data.employee_count, 10),
      };
      const { data: res } = await publicApi.submitApplication(payload);
      navigate('/thank-you', { state: { reference: res.reference } });
    } catch (err) {
      const msg = err.response?.data?.error || 'Submission failed. Please try again.';
      setErrors({ submit: msg });
    } finally { setSubmitting(false); }
  };

  const pct = ((step + 1) / 5) * 100;

  const Input = ({ field, ...props }) => (
    <input
      value={data[field]} onChange={e => set(field, e.target.value)}
      className={errors[field] ? 'is-error' : ''}
      {...props}
    />
  );
  const Textarea = ({ field, ...props }) => (
    <textarea
      value={data[field]} onChange={e => set(field, e.target.value)}
      className={errors[field] ? 'is-error' : ''}
      {...props}
    />
  );

  return (
    <div className="apply-page">
      {/* Top nav */}
      <nav className="apply-nav">
        <a href="/" className="apply-nav__logo">← Adeona</a>
        <span className="apply-nav__title">SME Initiative Application</span>
      </nav>

      {/* Progress */}
      <div className="apply-progress">
        <div className="progress-bar"><div className="progress-bar__fill" style={{ width: `${pct}%` }} /></div>
        <div className="progress-labels">
          {STEPS.map((s, i) => (
            <span key={s} className={i <= step ? 'done' : ''}>{i + 1}</span>
          ))}
        </div>
        <div className="progress-section">{STEPS[step]}</div>
      </div>

      <div className="apply-form">
        {/* ── Section A ── */}
        {step === 0 && (
          <>
            <div className="form-section-header">
              <h2>Company Details</h2>
              <p>Tell us about your business</p>
            </div>
            <Field label="Company Name" required error={errors.company_name}>
              <Input field="company_name" type="text" placeholder="e.g. Silva Traders (Pvt) Ltd" />
            </Field>
            <Field label="Industry" required error={errors.industry}>
              <select value={data.industry} onChange={e => set('industry', e.target.value)} className={errors.industry ? 'is-error' : ''}>
                <option value="">Select industry...</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </Field>
            <div className="field-row">
              <Field label="Contact Person" required error={errors.contact_person}>
                <Input field="contact_person" type="text" placeholder="Full name" />
              </Field>
              <Field label="Designation">
                <Input field="designation" type="text" placeholder="e.g. Director" />
              </Field>
            </div>
            <div className="field-row">
              <Field label="Mobile Number" required error={errors.mobile_number}>
                <Input field="mobile_number" type="tel" placeholder="07X XXXXXXX" />
              </Field>
              <Field label="Email Address" required error={errors.email}>
                <Input field="email" type="email" placeholder="you@company.lk" />
              </Field>
            </div>
            <div className="field-row">
              <Field label="District" required error={errors.district}>
                <select value={data.district} onChange={e => set('district', e.target.value)} className={errors.district ? 'is-error' : ''}>
                  <option value="">Select district...</option>
                  {DISTRICTS.map(d => <option key={d}>{d}</option>)}
                </select>
              </Field>
              <Field label="Number of Employees" required error={errors.employee_count}>
                <Input field="employee_count" type="number" min="1" placeholder="e.g. 15" />
              </Field>
            </div>
            <Field label="Website or Facebook Page">
              <Input field="website_url" type="text" placeholder="https://..." />
            </Field>
            <Field label="Business Registration Number" hint="Optional">
              <Input field="business_registration_number" type="text" placeholder="PV XXXXXX" />
            </Field>
          </>
        )}

        {/* ── Section B ── */}
        {step === 1 && (
          <>
            <div className="form-section-header">
              <h2>Business Challenge</h2>
              <p>Describe the problem you need solved</p>
            </div>
            <Field label="What is your #1 manual process costing you time or money?" required error={errors.manual_process_problem}>
              <Textarea field="manual_process_problem" placeholder="Describe the specific challenge in detail — what goes wrong, how often, what it costs..." />
            </Field>
            <Field label="How is this process currently handled?" required error={errors.current_process}>
              <Textarea field="current_process" placeholder="Walk us through your current process step by step..." />
            </Field>
            <div className="field-row">
              <Field label="Time spent on this per week" required error={errors.weekly_time_spent}>
                <Input field="weekly_time_spent" type="text" placeholder="e.g. 10 hours" />
              </Field>
              <Field label="Approximate monthly cost/loss (LKR)">
                <Input field="estimated_monthly_loss" type="text" placeholder="e.g. 50,000" />
              </Field>
            </div>
            <Field label="Current tools used">
              <div className="choice-group">
                {TOOLS.map(t => (
                  <CheckItem key={t} label={t}
                    checked={data.current_tools.includes(t)}
                    onChange={() => toggleArr('current_tools', t)} />
                ))}
              </div>
            </Field>
          </>
        )}

        {/* ── Section C ── */}
        {step === 2 && (
          <>
            <div className="form-section-header">
              <h2>Expected Impact</h2>
              <p>If this were solved, how would your business change?</p>
            </div>
            <Field label="Describe the ideal outcome" required error={errors.expected_automation_impact}>
              <Textarea field="expected_automation_impact" style={{ minHeight: 110 }}
                placeholder="How would your day-to-day operations change? What would you do with the time saved?" />
            </Field>
            <Field label="Expected measurable improvements" required error={errors.measurable_improvements}>
              <div className="choice-group">
                {IMPROVEMENTS.map(t => (
                  <CheckItem key={t} label={t}
                    checked={data.measurable_improvements.includes(t)}
                    onChange={() => toggleArr('measurable_improvements', t)} />
                ))}
              </div>
            </Field>
          </>
        )}

        {/* ── Section D ── */}
        {step === 3 && (
          <>
            <div className="form-section-header">
              <h2>Selection Justification</h2>
              <p>Help us understand why your business should be chosen</p>
            </div>
            <Field label="Why should your business be selected?" required error={errors.selection_reason}>
              <Textarea field="selection_reason" style={{ minHeight: 130 }}
                placeholder="Tell us your story. What makes your business and this challenge unique? Why would this solution have the greatest impact?" />
            </Field>
            <Field label="What broader impact would this project create?">
              <div className="choice-group">
                {BROAD_IMPACT.map(t => (
                  <CheckItem key={t} label={t}
                    checked={data.expected_business_impact.includes(t)}
                    onChange={() => toggleArr('expected_business_impact', t)} />
                ))}
              </div>
            </Field>
          </>
        )}

        {/* ── Section E ── */}
        {step === 4 && (
          <>
            <div className="form-section-header">
              <h2>Consent & Preferences</h2>
              <p>Almost done — a few final questions</p>
            </div>
            <Field label="If the pilot is successful, would you invest in future enhancements?" required error={errors.future_investment_interest}>
              <div className="choice-group">
                {[['yes','Yes, definitely'],['maybe','Maybe, depending on the value'],['no','Not at this time']].map(([v,l]) => (
                  <CheckItem key={v} label={l} type="radio"
                    checked={data.future_investment_interest === v}
                    onChange={() => set('future_investment_interest', v)} />
                ))}
              </div>
            </Field>
            <Field label="Willing to participate in testimonials, photos & success stories?" required error={errors.publicity_consent}>
              <div className="choice-group">
                {[['yes','Yes, I\'m happy to be featured'],['no','No, I prefer to stay private']].map(([v,l]) => (
                  <CheckItem key={v} label={l} type="radio"
                    checked={data.publicity_consent === v}
                    onChange={() => set('publicity_consent', v)} />
                ))}
              </div>
            </Field>
            <Field label="Would you like a free consultation even if not selected?">
              <div className="choice-group">
                <CheckItem label="Yes please" type="radio"
                  checked={data.consultation_interest === true}
                  onChange={() => set('consultation_interest', true)} />
                <CheckItem label="No thanks" type="radio"
                  checked={data.consultation_interest === false && data.consultation_interest !== ''}
                  onChange={() => set('consultation_interest', false)} />
              </div>
            </Field>
            <Field label="Areas of interest (select all that apply)">
              <div className="choice-group">
                {SERVICES.map(t => (
                  <CheckItem key={t} label={t}
                    checked={data.service_interests.includes(t)}
                    onChange={() => toggleArr('service_interests', t)} />
                ))}
              </div>
            </Field>
            <div className="terms-box">
              By submitting, you agree to allow Adeona Technologies to contact you regarding your application
              and to process your data for this initiative.
            </div>
            {errors.submit && <div className="err-msg" style={{marginBottom:12}}>{errors.submit}</div>}
          </>
        )}

        {/* Navigation */}
        <div className="form-nav">
          {step > 0 && (
            <button className="btn btn--outline" onClick={back}>← Back</button>
          )}
          {step < 4
            ? <button className="btn btn--primary" style={{flex:2}} onClick={next}>Continue →</button>
            : <button className="btn btn--primary" style={{flex:2}} onClick={submit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Application ✓'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
